import { useState } from 'react'
import type { DimensionResult, DimensionId, ModeResult, OverallStabilityType, StabilityType, TypeCode } from '@mindtype/shared'
import {
  DIMENSION_INFO,
  getPoleInterpretation,
  getContextSwitchInsight,
  TYPE_INTERPRETATIONS,
  STABILITY_INTERPRETATIONS,
} from '@mindtype/shared'

interface ColorConfig {
  primary: string
  secondary: string
  text: string
}

interface SelfKnowledgeModuleProps {
  typeCode: TypeCode
  modes: ModeResult
  dimensions: DimensionResult[]
  overallStability: OverallStabilityType
  stableAxes: DimensionId[]
  flexibleAxes: { dimension: DimensionId; pattern: StabilityType }[]
  colorConfig?: ColorConfig
}

const DEFAULT_COLOR = { primary: '#3b82f6', secondary: '#60a5fa', text: '#ffffff' }

export function SelfKnowledgeModule({
  typeCode,
  modes,
  dimensions,
  stableAxes,
  flexibleAxes,
  colorConfig = DEFAULT_COLOR,
}: SelfKnowledgeModuleProps) {
  const color = colorConfig
  const typeInfo = TYPE_INTERPRETATIONS[typeCode]
  const [expandedMode, setExpandedMode] = useState<'natural' | 'work' | 'stress' | null>('natural')

  // 统一的卡片样式
  const cardStyle = {
    border: `1px solid ${color.primary}15`,
  }

  const cardHeaderStyle = {
    borderBottom: `1px solid ${color.primary}15`,
    backgroundColor: `${color.primary}05`,
  }

  // 获取维度信息
  const getDimensionInfo = (dimensionId: DimensionId, pole: string) => {
    const dimInfo = DIMENSION_INFO[dimensionId]
    const poleName = pole === dimInfo.poleA.letter ? dimInfo.poleA.name : dimInfo.poleB.name
    const poleInterpretation = getPoleInterpretation(dimensionId, pole)
    return { dimInfo, poleName, poleInterpretation }
  }

  // 获取情境表现数据
  const getModeData = (mode: 'natural' | 'work' | 'stress') => {
    const modeType = modes[mode]
    const behaviors: string[] = []

    dimensions.forEach(dim => {
      const pole = dim[mode].resultLetter
      const interpretation = getPoleInterpretation(dim.dimensionId, pole)
      if (interpretation && interpretation.behaviors.length > 0) {
        behaviors.push(interpretation.behaviors[0])
      }
    })

    // 获取切换洞察
    const switchInsights: string[] = []
    if (mode === 'work' || mode === 'stress') {
      dimensions.forEach(dim => {
        const naturalPole = dim.natural.resultLetter
        const currentPole = dim[mode].resultLetter
        if (naturalPole !== currentPole) {
          const insight = getContextSwitchInsight(
            dim.dimensionId,
            naturalPole,
            currentPole,
            mode === 'work' ? 'work' : 'stress'
          )
          switchInsights.push(insight.insight)
        }
      })
    }

    return { modeType, behaviors, switchInsights }
  }

  const modeLabels = {
    natural: { name: '自然状态', desc: '日常生活中的典型表现' },
    work: { name: '工作状态', desc: '职场/学业中的典型表现' },
    stress: { name: '应激状态', desc: '压力下的典型反应' },
  }

  return (
    <div className="space-y-6">
      {/* ===== 1. 整体概述 ===== */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden" style={cardStyle}>
        <div className="px-6 py-4" style={cardHeaderStyle}>
          <h2 className="text-lg font-semibold" style={{ color: color.primary }}>整体概述</h2>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-700 leading-relaxed">
            {typeInfo?.description}
          </p>
        </div>
      </div>

      {/* ===== 2. 核心特质 ===== */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden" style={cardStyle}>
        <div className="px-6 py-4" style={cardHeaderStyle}>
          <h2 className="text-lg font-semibold" style={{ color: color.primary }}>核心特质</h2>
          <p className="text-sm text-gray-500 mt-1">四个维度的具体表现</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {dimensions.map(dim => {
              const { dimInfo, poleName, poleInterpretation } = getDimensionInfo(
                dim.dimensionId,
                dim.natural.resultLetter
              )
              const isStable = stableAxes.includes(dim.dimensionId)

              return (
                <div
                  key={dim.dimensionId}
                  className="rounded-lg p-4"
                  style={{ backgroundColor: `${color.primary}05`, border: `1px solid ${color.primary}15` }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className="px-2 py-1 rounded text-xs font-semibold"
                      style={{ backgroundColor: color.primary, color: color.text }}
                    >
                      {dim.natural.resultLetter}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{dimInfo.name}</span>
                    <span className="text-sm" style={{ color: color.primary }}>{poleName}</span>
                    {isStable && (
                      <span
                        className="ml-auto text-xs px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${color.primary}15`, color: color.primary }}
                      >
                        稳定
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {poleInterpretation?.summary}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* ===== 3. 情境表现 ===== */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden" style={cardStyle}>
        <div className="px-6 py-4" style={cardHeaderStyle}>
          <h2 className="text-lg font-semibold" style={{ color: color.primary }}>情境表现</h2>
          <p className="text-sm text-gray-500 mt-1">不同情境下的认知模式变化</p>
        </div>
        <div className="p-6">
          {/* 三种模式标签 */}
          <div className="flex gap-2 mb-4">
            {(['natural', 'work', 'stress'] as const).map(mode => {
              const modeData = getModeData(mode)
              const isExpanded = expandedMode === mode
              return (
                <button
                  key={mode}
                  onClick={() => setExpandedMode(isExpanded ? null : mode)}
                  className="flex-1 px-4 py-3 rounded-lg transition-all text-left"
                  style={{
                    backgroundColor: isExpanded ? color.primary : `${color.primary}08`,
                    color: isExpanded ? color.text : color.primary,
                  }}
                >
                  <div className="text-sm font-medium">{modeLabels[mode].name}</div>
                  <div
                    className="text-lg font-mono font-bold mt-1"
                    style={{ color: isExpanded ? color.text : color.primary }}
                  >
                    {modeData.modeType}
                  </div>
                </button>
              )
            })}
          </div>

          {/* 展开内容 */}
          {expandedMode && (
            <div
              className="rounded-lg p-4 space-y-4"
              style={{ backgroundColor: `${color.primary}05`, border: `1px solid ${color.primary}15` }}
            >
              <div className="text-sm text-gray-500">{modeLabels[expandedMode].desc}</div>

              {/* 行为表现 */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">典型表现</h4>
                <ul className="space-y-2">
                  {getModeData(expandedMode).behaviors.slice(0, 3).map((behavior, i) => (
                    <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                      <span style={{ color: color.primary }} className="mt-1">•</span>
                      <span>{behavior}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* 切换洞察 */}
              {expandedMode !== 'natural' && getModeData(expandedMode).switchInsights.length > 0 && (
                <div
                  className="rounded-lg p-3 mt-3"
                  style={{ backgroundColor: `${color.primary}08` }}
                >
                  <h4 className="text-sm font-medium mb-2" style={{ color: color.primary }}>情境切换</h4>
                  <ul className="space-y-2">
                    {getModeData(expandedMode).switchInsights.map((insight, i) => (
                      <li key={i} className="text-sm text-gray-600">{insight}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* 稳定性说明 */}
              {expandedMode === 'natural' && (
                <div
                  className="rounded-lg p-3 mt-3"
                  style={{ backgroundColor: `${color.primary}08` }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium"
                      style={{ backgroundColor: `${color.primary}20`, color: color.primary }}
                    >
                      {stableAxes.length}
                    </span>
                    <span className="text-sm font-medium" style={{ color: color.primary }}>
                      个维度跨情境稳定
                    </span>
                  </div>
                  {flexibleAxes.length > 0 && (
                    <div className="text-sm text-gray-600">
                      {flexibleAxes.map(({ dimension, pattern }) => {
                        const dimInfo = DIMENSION_INFO[dimension]
                        const stabilityInfo = STABILITY_INTERPRETATIONS[pattern]
                        return (
                          <span key={dimension} className="mr-3">
                            {dimInfo.name}：{stabilityInfo.name}
                          </span>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ===== 4. 优势与盲区 ===== */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* 优势 */}
        {typeInfo?.strengths && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden" style={cardStyle}>
            <div className="px-6 py-4" style={{ ...cardHeaderStyle, backgroundColor: `${color.primary}08` }}>
              <h3 className="text-base font-semibold" style={{ color: color.primary }}>优势</h3>
            </div>
            <div className="p-6">
              <ul className="space-y-3">
                {typeInfo.strengths.map((item, i) => (
                  <li key={i} className="text-sm text-gray-700 flex items-start gap-3">
                    <span
                      className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                      style={{ backgroundColor: color.primary }}
                    ></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* 盲区 */}
        {typeInfo?.challenges && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden" style={cardStyle}>
            <div className="px-6 py-4" style={{ ...cardHeaderStyle, backgroundColor: `${color.secondary}10` }}>
              <h3 className="text-base font-semibold" style={{ color: color.secondary }}>可能需要留意</h3>
            </div>
            <div className="p-6">
              <ul className="space-y-3">
                {typeInfo.challenges.map((item, i) => (
                  <li key={i} className="text-sm text-gray-700 flex items-start gap-3">
                    <span
                      className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                      style={{ backgroundColor: color.secondary }}
                    ></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* ===== 5. 成长线索 ===== */}
      {typeInfo?.growthTips && typeInfo.growthTips.length > 0 && (
        <div
          className="rounded-xl p-6"
          style={{ backgroundColor: `${color.primary}05`, border: `1px solid ${color.primary}15` }}
        >
          <h3 className="text-base font-semibold mb-4" style={{ color: color.primary }}>成长线索</h3>
          <p className="text-sm text-gray-500 mb-4">
            以下是一些自我观察的方向，不做具体指导：
          </p>
          <div className="space-y-3">
            {typeInfo.growthTips.slice(0, 2).map((tip, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-3 rounded-lg bg-white"
                style={{ border: `1px solid ${color.primary}10` }}
              >
                <span
                  className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0"
                  style={{ backgroundColor: `${color.primary}15`, color: color.primary }}
                >
                  {i + 1}
                </span>
                <span className="text-sm text-gray-700 leading-relaxed">{tip}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
